import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { HeaderCell, Table, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { Caption, H3, PageContent, PageHeader, PageSection } from 'common/styles/page';
import { poorColor, secondaryColor } from 'common/constants/globalstyles';
import { TrendDown, TrendUp } from 'common/components/icons';
import CategorySelect from 'common/components/CategorySelect';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';

import {
  useApiPortalV1AssessmentsResultsListQuery,
  useGetAssessmentPeriodMetricResultsQuery,
} from 'areas/assessment-schedule/api';
import { Icon } from 'semantic-ui-react';
import { useSearchParams } from 'react-router-dom';
import ImpactPie from 'common/components/charts/ImpactPie';
import ToggleControl from 'common/components/ToggleChartControl';
import AnalyticsInsights from './AnalyticsInsights';
import { getSelectedAssessmentPeriodCategories, getSelectedAssessmentPeriodId } from 'common/selectors/auth';
import { calculateImpact, calculateDelta } from '../utils/AnalyticsUtils';

import { AggregateType } from 'common/constants/AggregateType';
ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

const AnalyticsInsightsTwoFactor: React.FC = () => {
  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);

  const { data: data_by_cartesian } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.CARTESIAN_AVERAGES,
    assessmentPeriodId: assessmentPeriodId,
  });
  const { data: data_by_category } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.CATEGORY_OVERALL,
    assessmentPeriodId: assessmentPeriodId,
  });
  const { data: orgData } = useGetAssessmentPeriodMetricResultsQuery(
    { assessmentPeriodId: assessmentPeriodId },
    { skip: !assessmentPeriodId }
  );

  const [searchParams] = useSearchParams();

  const option = searchParams.get('option');
  const assessmentPeriodCategories = useSelector(getSelectedAssessmentPeriodCategories);
  useEffect(() => {
    if (option && option === 'str') setSortConfig({ key: 'overall_score', direction: 'desc' });
    else if (option && option === 'def') setSortConfig({ key: 'overall_score', direction: 'asc' });
  }, [option]);

  type CategoryOption = { value: string; label: string };
  const [selectedCategory1, setSelectedCategory1] = useState<string | null>(null);
  const [selectedCategory2, setSelectedCategory2] = useState<string | null>(null);
  const [chartType, setChartType] = useState('multi');

  const [insightsData, setInsightsData] = useState([]);
  const [overallScore, setOverallScore] = useState<number | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'category_name',
    direction: 'asc',
  });

  useEffect(() => {
    const score = orgData?.overall_score;
    setOverallScore(typeof score === 'number' ? score : null);
  }, [orgData]);

  useEffect(() => {
    const categories: Array<CategoryOption & { priority?: string }> = assessmentPeriodCategories?.length
      ? assessmentPeriodCategories.map((category: any) => ({
        value: category.id,
        label: category.name,
        priority: category.priority,
      }))
      : data_by_category && data_by_category.length > 0
        ? Array.from(
          new Map<string, CategoryOption & { priority?: string }>(
            data_by_category[0]?.result.aggregate_scores.map(
              (score: { category_name: string; category_id: string }) => [
                score.category_id,
                { value: score.category_id, label: score.category_name },
              ]
            )
          ).values()
        ).sort((a, b) => a.label.localeCompare(b.label))
        : [];

    if (categories.length === 0) return;

    const sortedCategories = [...categories].sort((a, b) => a.label.localeCompare(b.label));
    const selectedCategory1StillExists = sortedCategories.some(cat => cat.value === selectedCategory1);
    const selectedCategory2StillExists = sortedCategories.some(cat => cat.value === selectedCategory2);

    const primaryCategory = sortedCategories.find(cat => cat.priority === 'primary');
    const defaultCategory1Id = primaryCategory?.value || sortedCategories[0]?.value || null;
    const defaultCategory2Id = sortedCategories.find(cat => cat.priority !== 'primary')?.value || sortedCategories[0]?.value || null;

    if (!selectedCategory1 || !selectedCategory1StillExists) {
      setSelectedCategory1(defaultCategory1Id);
    }
    if (!selectedCategory2 || !selectedCategory2StillExists) {
      setSelectedCategory2(defaultCategory2Id);
    }
  }, [assessmentPeriodCategories, data_by_category, selectedCategory1, selectedCategory2]);

  useEffect(() => {
    if (data_by_cartesian && overallScore !== null) {
      const maxImpact = Math.max(
        ...data_by_cartesian[0]?.result?.aggregate_scores.map((item: any) =>
          calculateImpact(calculateDelta(item.overall_score, overallScore), item.average_n)
        )
      );
      const minImpact = Math.min(
        ...data_by_cartesian[0]?.result?.aggregate_scores.map((item: any) =>
          calculateImpact(calculateDelta(item.overall_score, overallScore), item.average_n)
        )
      );

      const filteredInsightsData = data_by_cartesian[0]?.result?.aggregate_scores
        ?.filter(
          (category: { category1_id: string; category2_id: string }) =>
            category.category1_id === selectedCategory1 &&
            category.category2_id === selectedCategory2
        )
        ?.map((item: any) => ({
          ...item,
          impact:
            Math.round(
              calculateImpact(calculateDelta(item.overall_score, overallScore), item.average_n) * 10
            ) / 10,
          impact_percent:
            item.overall_score < overallScore
              ? (calculateImpact(calculateDelta(item.overall_score, overallScore), item.average_n) /
                minImpact) *
              -100
              : (calculateImpact(calculateDelta(item.overall_score, overallScore), item.average_n) /
                maxImpact) *
              100,
          delta: Math.round(calculateDelta(item.overall_score, overallScore) * 10) / 10,
        }));
      setInsightsData(filteredInsightsData);
    }
  }, [data_by_cartesian, selectedCategory1, selectedCategory2, overallScore]);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = [...insightsData].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Using calculateImpact and calculateDelta from AnalyticsUtils

  const DifferenceCell = (score: number, percentageDifference: number): JSX.Element => {
    const direction =
      percentageDifference < 0 ? 'below norm' : percentageDifference > 0 ? 'above norm' : 'at norm';

    const trendIco =
      percentageDifference < 0 ? (
        <TrendIco>{TrendDown(poorColor)}</TrendIco>
      ) : percentageDifference > 0 ? (
        <TrendIco>{TrendUp(secondaryColor)}</TrendIco>
      ) : (
        <></>
      );

    return (
      <>
        <H3>
          {score.toFixed(1)} &nbsp;
          <span
            style={{
              ...(percentageDifference < 0 && { color: poorColor }),
              ...(percentageDifference > 0 && { color: secondaryColor }),
            }}
          >
            ({trendIco}
            {`${percentageDifference.toFixed(1)}% ${direction})`}
          </span>
        </H3>
      </>
    );
  };

  const SortUp = (): JSX.Element => (
    <Icon name="caret up" style={{ margin: 0, marginTop: -8, zIndex: 10 }} id={'label'} />
  );
  const SortDown = (): JSX.Element => (
    <Icon name="caret down" style={{ margin: 0, marginTop: -8, zIndex: 10 }} id={'label'} />
  );

  return chartType === 'multi' ? (
    <PageContent>
      <PageSection>
        <PageHeader
          title="Multi-Factor Insights"
          caption="Key factors impacting culture score and areas to prioritize for improvement."
          controls={
            <>
              <CategorySelect
                categories={assessmentPeriodCategories || []}
                selectedCategory={selectedCategory1}
                onCategoryChange={setSelectedCategory1}
                placeholder="Select Category"
                width="250px"
              />
              <CategorySelect
                categories={assessmentPeriodCategories || []}
                selectedCategory={selectedCategory2}
                onCategoryChange={setSelectedCategory2}
                placeholder="Select Category"
                width="250px"
                defaultCategoryId={
                  assessmentPeriodCategories?.find((category: any) => category.priority !== 'primary')?.id ||
                  assessmentPeriodCategories?.[0]?.id ||
                  null
                }
              />
              <ToggleControl
                buttonLabels={[
                  { type: 'insights', icon: 'eye' },
                  { type: 'multi', icon: 'random' },
                ]}
                activeToggle={chartType}
                setChartType={setChartType}
              />
            </>
          }
        />

        <Table>
          <TableHeader>
            <HeaderCell width={4} selectable onClick={() => handleSort('option1_name')}>
              Category 1{' '}
              {sortConfig.key === 'option1_name' &&
                (sortConfig.direction === 'asc' ? SortUp() : SortDown())}
            </HeaderCell>
            <HeaderCell width={4} selectable onClick={() => handleSort('option2_name')}>
              Category 2{' '}
              {sortConfig.key === 'option2_name' &&
                (sortConfig.direction === 'asc' ? SortUp() : SortDown())}
            </HeaderCell>
            <HeaderCell width={4} selectable onClick={() => handleSort('overall_score')}>
              Average Score{' '}
              {sortConfig.key === 'overall_score' &&
                (sortConfig.direction === 'asc' ? SortUp() : SortDown())}
            </HeaderCell>
            <HeaderCell width={2} selectable justify="center" onClick={() => handleSort('average_n')}>
              Population{' '}
              {sortConfig.key === 'average_n' &&
                (sortConfig.direction === 'asc' ? SortUp() : SortDown())}
            </HeaderCell>
            <HeaderCell width={2} selectable justify="center" onClick={() => handleSort('impact')}>
              Impact{' '}
              {sortConfig.key === 'impact' &&
                (sortConfig.direction === 'asc' ? SortUp() : SortDown())}
            </HeaderCell>
          </TableHeader>
          {overallScore &&
            sortedData.map(
              (
                row: {
                  option1_name: string;
                  category1_name: string;
                  option2_name: string;
                  category2_name: string;
                  overall_score: number;
                  average_n: number;
                  impact_percent: number;
                  delta: number;
                },
                index: React.Key
              ) => (
                <TableRow key={index}>
                  <TableCell width={4} $firstcol>
                    <InsightsText>{row.option1_name}</InsightsText>
                    <Caption>{row.category1_name}</Caption>
                  </TableCell>
                  <TableCell width={4} $firstcol>
                    <InsightsText>{row.option2_name}</InsightsText>
                    <Caption>{row.category2_name}</Caption>
                  </TableCell>
                  <TableCell width={4}>
                    <InsightsText>{DifferenceCell(row.overall_score, row.delta)}</InsightsText>
                  </TableCell>
                  <TableCell width={2} justify="center">
                    <InsightsText>{row.average_n}</InsightsText>
                  </TableCell>
                  <TableCell width={2} justify="center">
                    <InsightsText>
                      <ImpactPie value={row.impact_percent} size={40} />
                    </InsightsText>
                  </TableCell>
                </TableRow>
              )
            )}
        </Table>
      </PageSection>
    </PageContent>
  ) : (
    <AnalyticsInsights />
  );
};

export default AnalyticsInsightsTwoFactor;

const TrendIco = styled.div`
  display: inline-block;
  padding-right: 6px;
`;

const InsightsText = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

